import { useState, useEffect, useMemo } from "react";
import { Line } from "react-chartjs-2";
import { useApp } from "../context/AppContext";

export default function SeRankingMonitorView() {
  const { proj, activeId, getStore, setStore, dataRef, projects, setProjects, apiFetch,
    saveClusters, saveContent, saveStructure, saveSemClusters, saveLinks,
    flash, setView, view, dark, setDark, t, card, inp, btnP, btnG, btnSm, stitle, muted, th, td,
    userRole, currentUser, loadProjectData, openP, infoId, setInfoId, setActiveId } = useApp();
  const [serData, setSerData] = useState([]);
  const [serLoading, setSerLoading] = useState(true);
  const [serSyncing, setSerSyncing] = useState(false);
  const [serTab, setSerTab] = useState("t10"); // t10 | t3 | domain
  const [serSearch, setSerSearch] = useState("");
  const [serPage, setSerPage] = useState(0);
  const [serHideEmpty, setSerHideEmpty] = useState(false);
  const [serHideZero, setSerHideZero] = useState(false);
  const [serOnlyLow, setSerOnlyLow] = useState(false);
  const [serSortCol, setSerSortCol] = useState("growth_t10");
  const [serSortDir, setSerSortDir] = useState("asc");
  const PER_PAGE = 30;

  // Site selection state
  const [showSitePicker, setShowSitePicker] = useState(false);
  const [allSites, setAllSites] = useState([]);
  const [trackedIds, setTrackedIds] = useState(new Set());
  const [sitesLoading, setSitesLoading] = useState(false);
  const [siteFilter, setSiteFilter] = useState("");
  const [savingTracked, setSavingTracked] = useState(false);

  const token = localStorage.getItem("metago_token");
  const hdrs = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  const loadProjects = async () => {
    setSerLoading(true);
    try {
      const r = await fetch("/api/seranking/projects", { headers: hdrs });
      const d = await r.json();
      setSerData(Array.isArray(d) ? d : []);
    } catch (e) { console.error(e); }
    setSerLoading(false);
  };

  const loadTracked = async () => {
    try {
      const r = await fetch("/api/seranking/tracked", { headers: hdrs });
      const d = await r.json();
      setTrackedIds(new Set(Array.isArray(d) ? d : []));
    } catch (e) { console.error(e); }
  };

  useEffect(() => { loadProjects(); loadTracked(); }, []);

  const openSitePicker = async () => {
    setShowSitePicker(true);
    setSitesLoading(true);
    try {
      const r = await fetch("/api/seranking/sites", { headers: hdrs });
      const d = await r.json();
      setAllSites(Array.isArray(d) ? d : []);
    } catch (e) { console.error(e); }
    setSitesLoading(false);
  };

  const toggleSite = (id) => {
    setTrackedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const saveTracked = async () => {
    setSavingTracked(true);
    try {
      const ids = [...trackedIds];
      await fetch("/api/seranking/tracked", {
        method: "POST", headers: hdrs,
        body: JSON.stringify({ site_ids: ids, sites: allSites }),
      });
      setShowSitePicker(false);
    } catch (e) { console.error(e); }
    setSavingTracked(false);
  };

  const [syncProgress, setSyncProgress] = useState(null); // { current, total, domain, title, message }

  const handleSync = () => {
    if (trackedIds.size === 0) { setShowSitePicker(true); openSitePicker(); return; }
    setSerSyncing(true);
    setSyncProgress({ current: 0, total: 0, message: "Підключення..." });

    const token = localStorage.getItem("metago_token");
    const es = new EventSource(`/api/seranking/sync?token=${token}`);

    es.onmessage = (e) => {
      try {
        const d = JSON.parse(e.data);
        if (d.type === "start") {
          setSyncProgress({ current: 0, total: d.total, message: `0 / ${d.total}` });
        } else if (d.type === "progress") {
          setSyncProgress({ current: d.current, total: d.total, domain: d.domain, message: `${d.current} / ${d.total} — ${d.domain}` });
        } else if (d.type === "synced" || d.type === "skipped" || d.type === "error") {
          setSyncProgress({ current: d.current, total: d.total, domain: d.domain, message: `${d.current} / ${d.total} — ${d.domain}` });
        } else if (d.type === "status") {
          setSyncProgress(prev => ({ ...prev, message: d.message }));
        } else if (d.type === "done" || d.type === "fatal") {
          es.close();
          setSyncProgress(null);
          setSerSyncing(false);
          loadProjects();
        }
      } catch {}
    };

    es.onerror = () => {
      es.close();
      setSyncProgress(null);
      setSerSyncing(false);
      loadProjects();
    };
  };

  // Filter & sort
  let filtered = serData;
  if (serSearch) {
    const q = serSearch.toLowerCase();
    filtered = filtered.filter(r => r.domain.toLowerCase().includes(q) || r.title.toLowerCase().includes(q) || (r.manager || "").toLowerCase().includes(q));
  }
  if (serHideEmpty) filtered = filtered.filter(r => r.last_kw > 0);
  if (serHideZero) {
    if (serTab === "t10") filtered = filtered.filter(r => r.growth_t10 !== 0);
    else if (serTab === "t3") filtered = filtered.filter(r => r.growth_t3 !== 0);
  }
  if (serOnlyLow) filtered = filtered.filter(r => r.last_t10_pct < 20 && r.last_kw > 0);

  // Sort
  filtered = [...filtered].sort((a, b) => {
    const col = serSortCol;
    const mul = serSortDir === "asc" ? 1 : -1;
    if (col === "domain" || col === "title") return mul * (a[col] || "").localeCompare(b[col] || "");
    return mul * ((a[col] || 0) - (b[col] || 0));
  });

  const toggleSerSort = (col) => {
    if (serSortCol === col) setSerSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSerSortCol(col); setSerSortDir(col === "domain" || col === "title" ? "asc" : "desc"); }
    setSerPage(0);
  };
  const serSortIcon = (col) => serSortCol === col ? (serSortDir === "asc" ? " ↑" : " ↓") : "";

  const totalPages = Math.ceil(filtered.length / PER_PAGE);
  const paged = filtered.slice(serPage * PER_PAGE, (serPage + 1) * PER_PAGE);

  // Stats
  const totalProjects = serData.length;
  const uniqueDomains = new Set(serData.map(r => r.domain)).size;
  const grewT10 = serData.filter(r => r.growth_t10 > 0).length;
  const fellT10 = serData.filter(r => r.growth_t10 < 0).length;
  const avgT10 = totalProjects > 0 ? (serData.reduce((s, r) => s + r.growth_t10, 0) / totalProjects).toFixed(2) : 0;
  const avgT3 = totalProjects > 0 ? (serData.reduce((s, r) => s + r.growth_t3, 0) / totalProjects).toFixed(2) : 0;
  const lastUpdate = serData.length ? new Date(Math.max(...serData.map(r => new Date(r.updated_at)))).toLocaleDateString("uk-UA") : "—";

  // Guarantee check: 6+ months with growth_t10 < 20%
  const now = new Date();
  const getMonths = (d) => {
    if (!d) return 0;
    const start = new Date(d);
    return (now.getFullYear() - start.getFullYear()) * 12 + now.getMonth() - start.getMonth();
  };
  const guaranteeFail = serData.filter(r => getMonths(r.first_date) >= 6 && r.growth_t10 < 20);
  const guaranteeWarn = serData.filter(r => {
    const m = getMonths(r.first_date);
    return m >= 4 && m < 6 && r.growth_t10 < 20;
  });
  const isGuaranteeFail = (r) => getMonths(r.first_date) >= 6 && r.growth_t10 < 20;
  const isGuaranteeWarn = (r) => { const m = getMonths(r.first_date); return m >= 4 && m < 6 && r.growth_t10 < 20; };

  const GrowthArrow = ({ val }) => {
    if (val > 0) return <span style={{ color: "#10b981", fontWeight: 700 }}>+{val}% ▲</span>;
    if (val < 0) return <span style={{ color: "#ef4444", fontWeight: 700 }}>{val}% ▼</span>;
    return <span style={{ color: t.textMuted }}>0%</span>;
  };

  const PercentBar = ({ pct, color }) => (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <div style={{ width: 60, height: 6, borderRadius: 3, background: dark ? "#333" : "#e5e7eb", overflow: "hidden" }}>
        <div style={{ width: `${Math.min(pct, 100)}%`, height: "100%", borderRadius: 3, background: color || "#6366f1" }} />
      </div>
      <span style={{ fontSize: 11, color: t.textMuted }}>{pct}%</span>
    </div>
  );

  const statCard = (label, value, color) => (
    <div style={{ flex: 1, minWidth: 140, padding: "14px 16px", borderRadius: 12, background: dark ? "#1e1e2c" : "#f9fafb", border: `1px solid ${t.border}` }}>
      <div style={{ fontSize: 11, color: t.textMuted, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color: color || t.text }}>{value}</div>
    </div>
  );

  const tabBtn = (key, label) => (
    <button
      key={key}
      onClick={() => { setSerTab(key); setSerPage(0); setSerSortCol(key === "t10" ? "growth_t10" : key === "t3" ? "growth_t3" : "domain"); setSerSortDir(key === "domain" ? "asc" : "asc"); }}
      style={{
        padding: "6px 16px", borderRadius: 8, border: `1px solid ${serTab === key ? "#6366f1" : t.border}`,
        background: serTab === key ? (dark ? "#312e81" : "#eef2ff") : "transparent",
        color: serTab === key ? "#6366f1" : t.textMuted, fontWeight: 600, fontSize: 12, cursor: "pointer", fontFamily: "inherit"
      }}
    >
      {label}
    </button>
  );

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
        <button style={{ ...btnG, margin: 0 }} onClick={() => setView("list")}>← Проекти</button>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>SE Ranking Monitor</h2>
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 11, color: t.textMuted }}>Оновлено: {lastUpdate}</span>
        <button onClick={openSitePicker} style={btnG}>Обрати проекти ({trackedIds.size})</button>
        <button
          onClick={handleSync}
          disabled={serSyncing || trackedIds.size === 0}
          style={{ ...btnP, opacity: (serSyncing || trackedIds.size === 0) ? 0.6 : 1 }}
        >
          {serSyncing ? "Синхронізація..." : "Синхронізувати"}
        </button>
      </div>

      {/* Sync progress bar */}
      {syncProgress && (
        <div style={{ padding: "12px 16px", borderRadius: 12, background: dark ? "#1e1e2c" : "#eef2ff", border: `1px solid ${dark ? "#333366" : "#c7d2fe"}`, marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#6366f1", animation: "pulse 1.5s infinite" }} />
            <span style={{ fontSize: 13, fontWeight: 600, color: "#6366f1" }}>{syncProgress.message}</span>
          </div>
          {syncProgress.total > 0 && (
            <div style={{ width: "100%", height: 8, borderRadius: 4, background: dark ? "#333" : "#e5e7eb", overflow: "hidden" }}>
              <div style={{ width: `${Math.round((syncProgress.current / syncProgress.total) * 100)}%`, height: "100%", borderRadius: 4, background: "linear-gradient(90deg, #6366f1, #a855f7)", transition: "width 0.3s" }} />
            </div>
          )}
        </div>
      )}

      {/* Site picker modal */}
      {showSitePicker && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.5)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => setShowSitePicker(false)}>
          <div style={{ background: dark ? "#1a1a2e" : "#fff", borderRadius: 16, padding: 24, width: 560, maxHeight: "80vh", display: "flex", flexDirection: "column", boxShadow: "0 20px 60px rgba(0,0,0,.3)" }} onClick={e => e.stopPropagation()}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>Обрати проекти для моніторингу</h3>
              <div style={{ flex: 1 }} />
              <span style={{ fontSize: 12, color: t.textMuted }}>Обрано: {trackedIds.size}</span>
            </div>
            <input
              placeholder="Фільтр..."
              value={siteFilter}
              onChange={e => setSiteFilter(e.target.value)}
              style={{ padding: "8px 12px", borderRadius: 8, border: `1px solid ${t.border}`, background: dark ? "#16162a" : "#f9fafb", color: t.text, fontSize: 13, marginBottom: 12, fontFamily: "inherit" }}
            />
            <div style={{ flex: 1, overflowY: "auto", marginBottom: 14 }}>
              {sitesLoading ? (
                <div style={{ textAlign: "center", padding: 30, color: t.textMuted }}>Завантаження проектів з SE Ranking...</div>
              ) : (
                (allSites.filter(s => {
                  if (!siteFilter) return true;
                  const q = siteFilter.toLowerCase();
                  return s.title.toLowerCase().includes(q) || s.domain.toLowerCase().includes(q);
                })).map(s => (
                  <label key={s.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", borderRadius: 8, cursor: "pointer", background: trackedIds.has(s.id) ? (dark ? "#312e81" : "#eef2ff") : "transparent", marginBottom: 2 }}>
                    <input type="checkbox" checked={trackedIds.has(s.id)} onChange={() => toggleSite(s.id)} />
                    <span style={{ fontWeight: 600, fontSize: 13 }}>{s.title}</span>
                    <span style={{ fontSize: 11, color: t.textMuted }}>{s.domain}</span>
                  </label>
                ))
              )}
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button onClick={() => setShowSitePicker(false)} style={btnG}>Скасувати</button>
              <button onClick={saveTracked} disabled={savingTracked} style={{ ...btnP, opacity: savingTracked ? 0.6 : 1 }}>
                {savingTracked ? "Збереження..." : "Зберегти"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Guarantee alerts */}
      {guaranteeWarn.length > 0 && (
        <div style={{ padding: "14px 16px", borderRadius: 12, background: dark ? "#2d2305" : "#fffbeb", border: `2px solid ${dark ? "#92400e" : "#fcd34d"}`, marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
            <span style={{ fontSize: 16 }}>&#9200;</span>
            <span style={{ fontSize: 13, fontWeight: 800, color: "#f59e0b" }}>Наближаються до дедлайну ({guaranteeWarn.length})</span>
            <span style={{ fontSize: 11, color: t.textMuted }}>4-6 міс., приріст TOP-10 &lt; 20%</span>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {guaranteeWarn.sort((a, b) => a.growth_t10 - b.growth_t10).map(r => (
              <div key={r.id} onClick={() => { setSerSearch(r.domain); setSerPage(0); }}
                style={{ padding: "6px 12px", borderRadius: 8, background: dark ? "#1a1a2e" : "#fff", border: `1px solid ${dark ? "#92400e" : "#fcd34d"}`, cursor: "pointer", fontSize: 12 }}>
                <div style={{ fontWeight: 700 }}>{r.domain}</div>
                <div style={{ display: "flex", gap: 8, fontSize: 10, color: t.textMuted, marginTop: 2 }}>
                  <span>{r.manager || "—"}</span>
                  <span>{getMonths(r.first_date)} міс.</span>
                  <span style={{ color: "#f59e0b", fontWeight: 700 }}>T10: {r.growth_t10 > 0 ? "+" : ""}{r.growth_t10}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Stat cards */}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
        {statCard("Проектів", totalProjects)}
        {statCard("Доменів", uniqueDomains)}
        {statCard("Виросли T10", grewT10, "#10b981")}
        {statCard("Впали T10", fellT10, "#ef4444")}
        {statCard("Сер. приріст T10", `${avgT10}%`, Number(avgT10) >= 0 ? "#10b981" : "#ef4444")}
        {statCard("Сер. приріст T3", `${avgT3}%`, Number(avgT3) >= 0 ? "#10b981" : "#ef4444")}
      </div>

      {/* Manager ranking */}
      {(() => {
        const mgrs = {};
        serData.filter(r => r.manager).forEach(r => {
          if (!mgrs[r.manager]) mgrs[r.manager] = { projects: 0, grewT10: 0, fellT10: 0, grewT3: 0, fellT3: 0, sumT10: 0, sumT3: 0 };
          const m = mgrs[r.manager];
          m.projects++;
          m.sumT10 += r.growth_t10 || 0;
          m.sumT3 += r.growth_t3 || 0;
          if (r.growth_t10 > 0) m.grewT10++;
          if (r.growth_t10 < 0) m.fellT10++;
          if (r.growth_t3 > 0) m.grewT3++;
          if (r.growth_t3 < 0) m.fellT3++;
        });
        const ranked = Object.entries(mgrs).map(([name, m]) => {
          const avgT10 = Math.round((m.sumT10 / m.projects) * 100) / 100;
          const avgT3 = Math.round((m.sumT3 / m.projects) * 100) / 100;
          const score = Math.round((avgT3 * 0.6 + avgT10 * 0.4) * 100) / 100;
          return { name, ...m, avgT10, avgT3, score };
        }).sort((a, b) => b.score - a.score);
        if (!ranked.length) return null;
        const maxScore = Math.max(...ranked.map(r => Math.abs(r.score)), 1);
        return (
          <div style={{ padding: "16px", borderRadius: 12, background: dark ? "#1e1e2c" : "#f9fafb", border: `1px solid ${t.border}`, marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
              <span style={{ fontSize: 13, fontWeight: 700 }}>Рейтинг менеджерів</span>
              <span style={{ fontSize: 10, color: t.textMuted }}>(скор = T3 × 0.6 + T10 × 0.4)</span>
            </div>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              {ranked.map((m, i) => {
                const isGood = m.score > 0;
                const barW = Math.round((Math.abs(m.score) / maxScore) * 100);
                return (
                  <div key={m.name} onClick={() => { setSerSearch(m.name); setSerPage(0); }} style={{ flex: "1 1 220px", minWidth: 220, maxWidth: 320, padding: "10px 14px", borderRadius: 10, cursor: "pointer", background: dark ? "#16162a" : "#fff", border: `1px solid ${i === 0 ? "#10b981" : i === ranked.length - 1 ? "#ef4444" : t.border}`, position: "relative", overflow: "hidden" }}>
                    {i === 0 && <div style={{ position: "absolute", top: 0, right: 0, background: "#10b981", color: "#fff", fontSize: 9, fontWeight: 700, padding: "2px 8px", borderRadius: "0 0 0 8px" }}>TOP</div>}
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                      <span style={{ fontSize: 14, fontWeight: 800, color: t.textMuted, width: 20 }}>#{i + 1}</span>
                      <span style={{ fontSize: 13, fontWeight: 700 }}>{m.name}</span>
                    </div>
                    <div style={{ display: "flex", gap: 10, fontSize: 11, color: t.textMuted, marginBottom: 6 }}>
                      <span>{m.projects} пр.</span>
                      <span>T10: <span style={{ color: "#10b981" }}>+{m.grewT10}</span> <span style={{ color: "#ef4444" }}>-{m.fellT10}</span></span>
                      <span>T3: <span style={{ color: "#10b981" }}>+{m.grewT3}</span> <span style={{ color: "#ef4444" }}>-{m.fellT3}</span></span>
                    </div>
                    <div style={{ display: "flex", gap: 10, fontSize: 10, color: t.textMuted, marginBottom: 6 }}>
                      <span>T10 сер: <b style={{ color: m.avgT10 >= 0 ? "#10b981" : "#ef4444" }}>{m.avgT10 > 0 ? "+" : ""}{m.avgT10}%</b></span>
                      <span>T3 сер: <b style={{ color: m.avgT3 >= 0 ? "#10b981" : "#ef4444" }}>{m.avgT3 > 0 ? "+" : ""}{m.avgT3}%</b></span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div style={{ flex: 1, height: 6, borderRadius: 3, background: dark ? "#333" : "#e5e7eb", overflow: "hidden" }}>
                        <div style={{ width: `${barW}%`, height: "100%", borderRadius: 3, background: isGood ? "linear-gradient(90deg, #10b981, #34d399)" : "linear-gradient(90deg, #ef4444, #f87171)" }} />
                      </div>
                      <span style={{ fontSize: 13, fontWeight: 800, color: isGood ? "#10b981" : "#ef4444", minWidth: 55, textAlign: "right" }}>
                        {isGood ? "+" : ""}{m.score}%
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* Tabs + filters */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        {tabBtn("t10", "TOP-10 приріст")}
        {tabBtn("t3", "TOP-3 приріст")}
        {tabBtn("domain", "За доменом")}
        <span style={{ fontSize: 11, color: t.textMuted, marginLeft: 8 }}>Або клік по заголовку таблиці</span>
        <div style={{ flex: 1 }} />
        <input
          placeholder="Пошук..."
          value={serSearch}
          onChange={e => { setSerSearch(e.target.value); setSerPage(0); }}
          style={{ padding: "6px 12px", borderRadius: 8, border: `1px solid ${t.border}`, background: dark ? "#1a1a2e" : "#fff", color: t.text, fontSize: 12, width: 180, fontFamily: "inherit" }}
        />
        <label style={{ fontSize: 11, color: t.textMuted, display: "flex", alignItems: "center", gap: 4, cursor: "pointer" }}>
          <input type="checkbox" checked={serHideEmpty} onChange={e => { setSerHideEmpty(e.target.checked); setSerPage(0); }} /> Без даних
        </label>
        <label style={{ fontSize: 11, color: t.textMuted, display: "flex", alignItems: "center", gap: 4, cursor: "pointer" }}>
          <input type="checkbox" checked={serHideZero} onChange={e => { setSerHideZero(e.target.checked); setSerPage(0); }} /> 0% приріст
        </label>
        <label style={{ fontSize: 11, color: serOnlyLow ? "#ef4444" : t.textMuted, display: "flex", alignItems: "center", gap: 4, cursor: "pointer", fontWeight: serOnlyLow ? 700 : 400 }}>
          <input type="checkbox" checked={serOnlyLow} onChange={e => { setSerOnlyLow(e.target.checked); setSerPage(0); }} /> &lt;20% T10
        </label>
      </div>

      {serLoading ? (
        <div style={{ textAlign: "center", padding: 40, color: t.textMuted }}>Завантаження...</div>
      ) : paged.length === 0 ? (
        <div style={{ textAlign: "center", padding: 40, color: t.textMuted }}>
          {serData.length === 0 ? 'Немає даних. Натисніть "Синхронізувати" для завантаження.' : "Нічого не знайдено"}
        </div>
      ) : (
        <>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${t.border}`, textAlign: "left" }}>
                  <th style={{ padding: "8px 6px", color: t.textMuted, fontWeight: 600 }}>#</th>
                  <th style={{ padding: "8px 6px", color: t.textMuted, fontWeight: 600, cursor: "pointer" }} onClick={() => toggleSerSort("manager")}>Менеджер{serSortIcon("manager")}</th>
                  <th style={{ padding: "8px 6px", color: t.textMuted, fontWeight: 600, cursor: "pointer" }} onClick={() => toggleSerSort("domain")}>Домен{serSortIcon("domain")}</th>
                  <th style={{ padding: "8px 6px", color: t.textMuted, fontWeight: 600, cursor: "pointer" }} onClick={() => toggleSerSort("title")}>Проект{serSortIcon("title")}</th>
                  <th style={{ padding: "8px 6px", color: t.textMuted, fontWeight: 600 }}>Перше</th>
                  <th style={{ padding: "8px 6px", color: t.textMuted, fontWeight: 600 }}>Останнє</th>
                  <th style={{ padding: "8px 6px", color: t.textMuted, fontWeight: 600, cursor: "pointer" }} onClick={() => toggleSerSort("last_kw")}>KW{serSortIcon("last_kw")}</th>
                  <th style={{ padding: "8px 6px", color: t.textMuted, fontWeight: 600 }} colSpan={3}>TOP-3</th>
                  <th style={{ padding: "8px 6px", color: t.textMuted, fontWeight: 600 }} colSpan={4}>TOP-10</th>
                </tr>
                <tr style={{ borderBottom: `1px solid ${t.border}` }}>
                  <th colSpan={7} />
                  <th style={{ padding: "2px 6px", fontSize: 10, color: t.textMuted, cursor: "pointer" }} onClick={() => toggleSerSort("first_t3_pct")}>Перш.{serSortIcon("first_t3_pct")}</th>
                  <th style={{ padding: "2px 6px", fontSize: 10, color: t.textMuted, cursor: "pointer" }} onClick={() => toggleSerSort("last_t3_pct")}>Ост.{serSortIcon("last_t3_pct")}</th>
                  <th style={{ padding: "2px 6px", fontSize: 10, color: t.textMuted, cursor: "pointer", fontWeight: 700 }} onClick={() => toggleSerSort("growth_t3")}>+/−{serSortIcon("growth_t3")}</th>
                  <th style={{ padding: "2px 6px", fontSize: 10, color: t.textMuted, cursor: "pointer" }} onClick={() => toggleSerSort("first_t10_pct")}>Перш.{serSortIcon("first_t10_pct")}</th>
                  <th style={{ padding: "2px 6px", fontSize: 10, color: t.textMuted, cursor: "pointer" }} onClick={() => toggleSerSort("last_t10_pct")}>Ост.{serSortIcon("last_t10_pct")}</th>
                  <th style={{ padding: "2px 6px", fontSize: 10, color: t.textMuted, cursor: "pointer", fontWeight: 700 }} onClick={() => toggleSerSort("growth_t10")}>+/−{serSortIcon("growth_t10")}</th>
                  <th title="Зміна % TOP-10 між двома останніми знятіями" style={{ padding: "2px 6px", fontSize: 10, color: t.textMuted, cursor: "pointer", fontWeight: 700 }} onClick={() => toggleSerSort("delta_t10_pct")}>Δ період{serSortIcon("delta_t10_pct")}</th>
                </tr>
              </thead>
              <tbody>
                {paged.map((r, i) => (
                  <tr key={r.id} style={{ borderBottom: `1px solid ${t.border}`, background: isGuaranteeWarn(r) ? (dark ? "rgba(245,158,11,.1)" : "rgba(245,158,11,.06)") : i % 2 === 0 ? "transparent" : (dark ? "rgba(255,255,255,.02)" : "rgba(0,0,0,.02)"), borderLeft: isGuaranteeWarn(r) ? "3px solid #f59e0b" : "3px solid transparent" }}>
                    <td style={{ padding: "8px 6px", color: t.textMuted }}>{serPage * PER_PAGE + i + 1}</td>
                    <td style={{ padding: "8px 6px", fontSize: 11, whiteSpace: "nowrap", color: t.textMuted }}>{r.manager || "—"}</td>
                    <td style={{ padding: "8px 6px", fontWeight: 600, maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.last_t10_pct < 20 && r.last_kw > 0 && <span style={{ display: "inline-block", padding: "1px 5px", borderRadius: 4, background: dark ? "rgba(239,68,68,.15)" : "rgba(239,68,68,.1)", color: "#ef4444", fontSize: 9, fontWeight: 700, marginRight: 6, verticalAlign: "middle" }}>&lt;20%</span>}{r.domain}{r.site_count > 1 && <span title={(r.domains || []).join(" + ")} style={{ display: "inline-block", marginLeft: 6, padding: "1px 6px", borderRadius: 99, background: dark ? "rgba(99,102,241,.18)" : "rgba(99,102,241,.12)", color: "#6366f1", fontSize: 9.5, fontWeight: 700, verticalAlign: "middle" }}>×{r.site_count}</span>}</td>
                    <td style={{ padding: "8px 6px", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: t.textMuted }}>{r.title}</td>
                    <td style={{ padding: "8px 6px", fontSize: 11, color: t.textMuted }}>{r.first_date ? new Date(r.first_date).toLocaleDateString("uk-UA") : "—"}</td>
                    <td style={{ padding: "8px 6px", fontSize: 11, color: t.textMuted }}>{r.last_date ? new Date(r.last_date).toLocaleDateString("uk-UA") : "—"}</td>
                    <td style={{ padding: "8px 6px" }}>{r.last_kw}</td>
                    <td style={{ padding: "8px 6px" }}><PercentBar pct={r.first_t3_pct} color="#8b5cf6" /></td>
                    <td style={{ padding: "8px 6px" }}><PercentBar pct={r.last_t3_pct} color="#8b5cf6" /></td>
                    <td style={{ padding: "8px 6px" }}><GrowthArrow val={r.growth_t3} /></td>
                    <td style={{ padding: "8px 6px" }}><PercentBar pct={r.first_t10_pct} color="#6366f1" /></td>
                    <td style={{ padding: "8px 6px" }}><PercentBar pct={r.last_t10_pct} color="#6366f1" /></td>
                    <td style={{ padding: "8px 6px" }}><GrowthArrow val={r.growth_t10} /></td>
                    <td title={r.prev_date ? ("vs " + new Date(r.prev_date).toLocaleDateString("uk-UA")) : "немає другої точки"} style={{ padding: "8px 6px" }}>{r.delta_t10_pct == null ? <span style={{ color: t.textMuted, fontSize: 11 }}>—</span> : <GrowthArrow val={r.delta_t10_pct} />}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{ display: "flex", justifyContent: "center", gap: 4, marginTop: 16 }}>
              <button disabled={serPage === 0} onClick={() => setSerPage(p => p - 1)} style={{ ...btnSm, opacity: serPage === 0 ? 0.4 : 1 }}>←</button>
              {Array.from({ length: Math.min(totalPages, 10) }, (_, i) => {
                let p = i;
                if (totalPages > 10) {
                  const start = Math.max(0, Math.min(serPage - 4, totalPages - 10));
                  p = start + i;
                }
                return (
                  <button key={p} onClick={() => setSerPage(p)} style={{ ...btnSm, background: serPage === p ? "#6366f1" : "transparent", color: serPage === p ? "#fff" : t.textMuted, fontWeight: serPage === p ? 700 : 400, minWidth: 28 }}>{p + 1}</button>
                );
              })}
              <button disabled={serPage >= totalPages - 1} onClick={() => setSerPage(p => p + 1)} style={{ ...btnSm, opacity: serPage >= totalPages - 1 ? 0.4 : 1 }}>→</button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ═══ FIREHOSE VIEW ═══
